//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// login-btn imports
import { IContainer, IProps, IState } from "./LoginBtn.d";
import { loginBtnFragments } from "./LoginBtn.gql";
// other internals
import { logger } from "@util/logger.util";
import GoogleBtn from "../google-btn";
import { doUserLogin } from "@util/auth.util";
import { snackBarOpen } from "@actions/snack-bar.actions";
import ReduxState from "../../declarations/state.d";
import { resetOnboardingState } from "@state/local-storage";

//////////////////
// COMPONENT
//////////////////
export class LoginBtnBaseCmpt extends React.Component<IProps, IState> {
  private mountTime: number;

  constructor(props: IProps) {
    super(props);
    this.state = {
      loading: false,
    };
    this.mountTime = Date.now();
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    if (nextProps.authed && !this.props.authed) {
      this.forceUpdate();
      if (this.props.preventLoginRedirect) {
        return;
      }
      if (!this.props.router) {
        return;
      }
      if (this.props.isChromeExtension) {
        this.props.router.replace("/chrome.html");
      } else if (nextProps.redirect) {
        this.props.router.replace(`/${nextProps.redirect}`);
      } else {
        this.props.router.replace("/app");
      }
    }
  }

  private onGoogleSuccess(response: any) {
    const googleOfflineCode = response.code;
    const { relay, verificationType } = this.props;
    const input: ISchema.ILoginUserMutationInput = { googleOfflineCode };
    if (verificationType) {
      input.verificationType = verificationType as any;
    }
    this.setState({ loading: true });
    if (this.props.onStart) {
      this.props.onStart();
    }

    this.props.loginUserMutation(
      relay.environment,
      input,
      this.onRelaySuccess.bind(this),
      this.onRelayFailure.bind(this),
    );
  }

  private handleSuccess = (verifyCodeResult: boolean, verifyCodeMessage: string) => {
    if (this.props.onSuccess) {
      this.props.onSuccess(verifyCodeResult, verifyCodeMessage);
    }
  };

  private handleFailure = (error?: any) => {
    if (this.props.onFailure) {
      this.props.onFailure(error);
    }

    if (error && !this.props.hideSnackBarMessages) {
      this.props.snackBarOpen(error);
    }
  };

  private onGoogleFailure(error: any) {
    logger.error(`OAuth failure - ${error.error}`, error);
    switch (error.error) {
      case "idpiframe_initialization_failed":
        break;
      default:
        this.handleFailure("Sorry, failed to connect to your Google account.");
        break;
    }
    if (this.props.onGoogleFailure) {
      this.props.onGoogleFailure(error);
    }
  }

  private onRelaySuccess(payload: ISchema.ILoginUserMutationPayload) {
    if (
      payload &&
      payload.viewer &&
      payload.viewer.user &&
      payload.viewer.user.createdTime >= this.mountTime
    ) {
      resetOnboardingState();
    }

    this.setState({ loading: false });
    let badLoginStatusMessage = "";
    switch (payload.loginStatus) {
      // success!
      case "Success":
        doUserLogin(payload.viewer, payload.operation, payload.orgOperation);
        this.handleSuccess(payload.verifyCodeResult, payload.verifyCodeMessage);
        if (!this.props.hideSnackBarMessages) {
          this.props.snackBarOpen("Welcome to Clockwise!");
        }
        break;
      // successful user login, but bad status
      case "GoogleContactSharingDisabled":
        badLoginStatusMessage = "Please update Google to allow Clockwise to access your contacts.";
        this.handleSuccess(payload.verifyCodeResult, payload.verifyCodeMessage);
        break;
      // successful user login, but bad status
      case "GoogleApiAccessDisabled":
        badLoginStatusMessage = "Please update Google to allow Clockwise to have API access.";
        this.handleSuccess(payload.verifyCodeResult, payload.verifyCodeMessage);
        break;
      // generic failure of onboarding the org, has a user, but bad status
      case "NoCrawl":
        badLoginStatusMessage = "Sorry, there was a problem onboarding your account.";
        this.handleSuccess(payload.verifyCodeResult, payload.verifyCodeMessage);
        break;
      // no user, bad login status
      case "PersonalEmail":
        badLoginStatusMessage =
          "Please sign in with your work Google account. " +
          "Clockwise does not currently support signing in with a personal Google account.";
        this.handleFailure();
        break;
      // no user, bad login status
      case "WaitlistedEmail":
        badLoginStatusMessage =
          "Thanks for your interest in Clockwise. We are working hard " +
          "to increase access to our service. We have added you to our waitlist and will be in touch soon.";
        this.handleFailure();
        break;
      case "SecondaryDomainUsed":
        badLoginStatusMessage =
          "It appears you are using a secondary domain for your " +
          "organization. Please sign in using your Google account for your primary domain.";
        this.handleFailure();
        break;
      case "Deactivating":
        badLoginStatusMessage =
          "You recently deactivated your Clockwise account. While we process " +
          "your deactivation it is not possible to sign in. Apologies for the inconvenience, please try again later.";
        this.handleFailure();
        break;
      case "BlockedByAdmin":
        badLoginStatusMessage =
          "Your admin removed your access to Clockwise. To remove Clockwise from your calendar, " +
          "please delete the Chrome extension. If this was a mistake, contact your admins. ";
        this.handleFailure();
        break;
      // meh
      default:
        this.handleFailure("Missing login status, sorry something went wrong.");
        break;
    }

    // if we have a bad login status, display it for 20 seconds
    if (badLoginStatusMessage && !this.props.hideSnackBarMessages) {
      this.props.snackBarOpen(
        badLoginStatusMessage,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        20000,
      );
    }
  }

  private onRelayFailure(_response: any) {
    this.setState({ loading: false });
    this.handleFailure("Sorry, there was a problem signing in.");
  }

  public render() {
    return (
      <GoogleBtn
        style={this.props.style}
        labelStyle={this.props.labelStyle}
        labelColor={this.props.labelColor}
        backgroundColor={this.props.backgroundColor}
        primary={!!this.props.primary}
        label={this.props.label ? this.props.label : "Login"}
        disabled={this.state.loading}
        onSuccess={this.onGoogleSuccess.bind(this)}
        onFailure={this.onGoogleFailure.bind(this)}
        flat={this.props.flat}
        fullWidth={this.props.fullWidth}
        googleStandard={this.props.googleStandard}
        hideIcon={this.props.hideIcon}
        onClick={this.props.onClick}
      />
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: ReduxState.Redux, _ownProps: IContainer) {
  return {
    isChromeExtension: state.webExtension.isWebExtension,
    authed: state.auth.authed,
  };
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(LoginBtnBaseCmpt);

//////////////////
// RELAY
//////////////////
export const LoginBtnCmpt = createFragmentContainer<IContainer>(reduxContainer, loginBtnFragments);
